'use client'

import type { BalancesSummary } from '@polaris/core/models'
import type { VirtualItem } from '@tanstack/react-virtual'
import { useAtom } from 'jotai'
import { ArrowLeftIcon, ChevronDown, ChevronRight } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import {
  memo,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useKeyPressEvent, useMeasure } from 'react-use'
import { useDebounce } from 'use-debounce'
import { useTokenActions } from '@/src/hooks/token-info/use-token-actions'
import { useDisclosure } from '@/src/hooks/use-disclosure'
import { useVirtualRows } from '@/src/hooks/use-virtual-rows'
import { useAccountsFilteredBalances } from '@/src/queries/hooks/balances'
import { useSupportedTokens } from '@/src/queries/hooks/tokens'
import {
  useNetworkSelection,
  useTokenSelection,
} from '@/src/stores/token-selection/actions'
import {
  singleBalanceAccordionAtom,
  type TokenSelectionMetadata,
} from '@/src/stores/token-selection/atoms'
import { FRAMER_MOTION_TOKEN_SELECT_EASING } from '@/src/utils/consts'
import { cn } from '@/src/utils/styles'
import { BalanceSummariesAccordions } from '../balances'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion'
import { Button } from '../ui/button'
import { ChainImg } from '../ui/chain-img'
import { NoSearchResults } from '../ui/no-search-results'
import { SearchField } from '../ui/search-field'
import { Skeleton } from '../ui/skeleton'
import { TooltipProvider } from '../ui/tooltip'
import { FilterNetworkSelection } from './network/filter'
import { FilterButtonImage } from './network/filter-button'
import { PopularTokenItem } from './popular-token-item'
import type { OnSelectToken, PopularTokenEntry } from './types'

export const TokenSelection = () => {
  const {
    close,
    filteredNetwork,
    isOpen: isTokenSelectionOpen,
    setAccordionValue,
    open: openTokenSelection,
  } = useTokenSelection()
  const { isOpen: isNetworkSelectionOpen } = useNetworkSelection()
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query, 300)

  const ref = useRef<HTMLInputElement>(null)
  const [hasFocused, setHasFocused] = useState(false)

  const {
    isOpen: isNetworkFilterOpen,
    open: openNetworkFilter,
    close: closeNetworkFilter,
  } = useDisclosure()

  const [, setBalancesAccordionItem] = useAtom(singleBalanceAccordionAtom)

  const onViewAnimationComplete = useCallback(() => {
    if (!isTokenSelectionOpen) {
      setAccordionValue('your-balances')
      setBalancesAccordionItem('')
      setHasFocused(false)
      setQuery('')
    }
    if (isTokenSelectionOpen && ref.current && !hasFocused) {
      ref.current.focus()
      setHasFocused(true)
    }
  }, [
    hasFocused,
    isTokenSelectionOpen,
    setAccordionValue,
    setBalancesAccordionItem,
  ])

  const isAnyNetworkSelectionOpen =
    isNetworkFilterOpen || isNetworkSelectionOpen

  useKeyPressEvent('Escape', () => {
    if (
      isTokenSelectionOpen &&
      !isNetworkSelectionOpen &&
      !isNetworkFilterOpen
    ) {
      close()
    }
  })

  return (
    <motion.div
      className='absolute inset-0 z-40 flex flex-col bg-background min-w-96'
      onAnimationComplete={onViewAnimationComplete}
      animate={{ x: isTokenSelectionOpen ? 0 : '100%' }}
      initial={{ x: '100%' }}
      exit={{ x: '100%' }}
      transition={{ duration: 0.3, ease: FRAMER_MOTION_TOKEN_SELECT_EASING }}
    >
      <motion.div
        className='flex flex-col'
        onAnimationComplete={() => {
          if (!isAnyNetworkSelectionOpen && isTokenSelectionOpen) {
            ref.current?.focus()
            setHasFocused(true)
          }
        }}
        animate={{
          opacity: isAnyNetworkSelectionOpen ? 0 : 1,
          y: isAnyNetworkSelectionOpen ? '-10%' : 0,
        }}
        initial={{ y: 0 }}
        transition={{ duration: 0.3, ease: FRAMER_MOTION_TOKEN_SELECT_EASING }}
      >
        <div className='flex items-center justify-between gap-3 px-6 pt-6 pb-2'>
          <Button
            type='button'
            size='md'
            onClick={close}
            onFocus={() => {
              openTokenSelection('from')
            }}
          >
            <ArrowLeftIcon className='size-6 link' />
          </Button>
          <SearchField
            dimension='medium'
            inputProps={{
              placeholder: 'Search tokens',
              value: query,
              onChange: (e) => setQuery(e.target.value),
              ref,
            }}
            resetState={() => setQuery('')}
            className='max-w-[240px]'
          />
          <button
            type='button'
            onClick={openNetworkFilter}
            className='flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-4 rounded-xl'
          >
            <AnimatePresence>
              {filteredNetwork ? (
                <motion.div
                  className='relative'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.3,
                    ease: FRAMER_MOTION_TOKEN_SELECT_EASING,
                  }}
                >
                  <ChainImg
                    chainId={filteredNetwork.chain_id}
                    chainImg={filteredNetwork.chain_img}
                    chainName={filteredNetwork.chain_name}
                    width={32}
                    height={32}
                    containerClassName='min-w-12 size-12 rounded-xl'
                  />
                  <div className='absolute bottom-0 right-0 size-6 flex items-center justify-center bg-surface3-100 rounded-[6px]'>
                    <ChevronDown className='size-4 text-primary' />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.3,
                    ease: FRAMER_MOTION_TOKEN_SELECT_EASING,
                  }}
                >
                  <FilterButtonImage showChevron />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        </div>
        <div
          className={cn(
            'flex flex-col px-2 transition-opacity duration-300 ease-default',
            {
              'opacity-0': isAnyNetworkSelectionOpen,
            },
          )}
        >
          <VirtualizedSections query={debouncedQuery} />
        </div>
      </motion.div>
      <FilterNetworkSelection
        isOpen={isNetworkFilterOpen}
        onRequestClose={closeNetworkFilter}
      />
    </motion.div>
  )
}

const HORIZONTAL_PADDING = 100
const LABEL_HEIGHT = 44
const SEARCH_FIELD_HEIGHT = 80

function VirtualizedSections({ query }: { query: string }) {
  const { filteredNetwork, direction, close, setFilteredNetwork } =
    useTokenSelection()
  const { open: openNetworkSelection } = useNetworkSelection()
  const [yourBalancesHeightRef, { height: yourBalancesHeight }] =
    useMeasure<HTMLDivElement>()

  const paginatedTokensQuery = useSupportedTokens({
    query,
    chainId: filteredNetwork?.chain_id ?? '',
  })

  const { balancesSummary, totalBalance } = useAccountsFilteredBalances({
    query,
    chainId: filteredNetwork?.chain_id ?? '',
    includeWalletKeyring: true, // Include pending account balances for trading
  })

  const balanceSummariesDenoms = useMemo(
    () => balancesSummary.flatMap(({ summary }) => summary.denoms),
    [balancesSummary],
  )

  const isSearching = query !== ''

  const tokens = useMemo(() => {
    if (!paginatedTokensQuery.data) return []

    const tokens: PopularTokenEntry[] = paginatedTokensQuery.data.pages
      .flatMap((d) => d.assets)
      .filter((token) => {
        if (!token) return false
        const denoms = token.denoms_by_chain.flatMap(({ denom }) => denom)
        return denoms.some((d) => !balanceSummariesDenoms.includes(d))
      })
      .map((token) => ({
        ...token,
        isBalance: false,
      }))

    if (!isSearching) {
      return [...tokens]
    }

    const balancesTokens = balancesSummary.flatMap(({ balances }) =>
      balances.map((balance) => {
        return {
          denoms_by_chain: [
            {
              chain_id: balance.chain_id,
              denom: balance.denom,
              display_identifier: balance.display_name,
            },
          ],
          ticker: balance.ticker,
          variant_display_name: balance.display_name,
          variant_logo_img_url: balance.logo_img_url,
          preferred_chain_id: balance.chain_id,
          price_usd: balance.price_usd,
          isBalance: true,
        } as PopularTokenEntry
      }),
    )

    return [...balancesTokens, ...tokens]
  }, [
    balanceSummariesDenoms,
    balancesSummary,
    isSearching,
    paginatedTokensQuery.data,
  ])

  const { parentRef: tokensParentRef, rowVirtualizer: virtualizedTokensRows } =
    useVirtualRows({
      infiniteQuery: paginatedTokensQuery,
      rows: tokens,
      paddingStart: (!isSearching ? yourBalancesHeight : 0) + LABEL_HEIGHT,
      estimateSize: () => 64,
      overscan: 5,
    })

  // Use consolidated token actions hook
  const { selectToken } = useTokenActions({ direction: direction ?? 'from' })

  const onSelectBalanceToken = useCallback(
    ({ chainId, denom, ticker }: Partial<TokenSelectionMetadata>) => {
      selectToken({ chain_id: chainId, denom, ticker })
      setFilteredNetwork(null)
      close()
    },
    [close, setFilteredNetwork, selectToken],
  )

  const onSelectPopularToken = useCallback(
    (params: Partial<TokenSelectionMetadata>) => {
      if (filteredNetwork || params.shouldDirectlySelect) {
        selectToken({
          chain_id: params.chainId,
          denom: params.denom,
          ticker: params.ticker,
        })
        setFilteredNetwork(null)
        close()
      } else {
        openNetworkSelection({
          ...params,
          direction,
        })
      }
    },
    [
      filteredNetwork,
      setFilteredNetwork,
      close,
      openNetworkSelection,
      direction,
      selectToken,
    ],
  )

  const items = virtualizedTokensRows.getVirtualItems()

  const noSearchResults =
    !!query && !paginatedTokensQuery.isLoading && tokens.length === 0

  return (
    <div
      ref={tokensParentRef}
      className='flex flex-col h-full overflow-auto no-scrollbar '
      style={{
        maxHeight: `calc(100vh - ${
          HORIZONTAL_PADDING + SEARCH_FIELD_HEIGHT + LABEL_HEIGHT
        }px)`,
      }}
    >
      <div
        style={{
          height:
            noSearchResults || paginatedTokensQuery.isLoading
              ? 'auto'
              : `${virtualizedTokensRows.getTotalSize() + yourBalancesHeight}px`,
        }}
        className='relative w-full pb-16'
      >
        {noSearchResults && <NoSearchResults failedQuery={query} />}
        {!isSearching && (
          <YourBalances
            balancesSummary={balancesSummary}
            ref={yourBalancesHeightRef as unknown as RefObject<HTMLDivElement>}
            onSelectToken={onSelectBalanceToken}
            totalBalance={totalBalance}
          />
        )}
        <PopularTokens
          items={items}
          tokens={tokens}
          onSelectToken={onSelectPopularToken}
          isLoading={paginatedTokensQuery.isLoading}
          noSearchResults={noSearchResults}
          query={query}
        />
      </div>
    </div>
  )
}

function YourBalances_({
  balancesSummary,
  ref,
  onSelectToken,
}: {
  balancesSummary: BalancesSummary[]
  ref?: RefObject<HTMLDivElement>
  onSelectToken: OnSelectToken
  totalBalance: number
}) {
  const {
    filteredNetwork,
    setFilteredNetwork,
    accordionValue,
    setAccordionValue,
    direction,
    isOpen,
  } = useTokenSelection()

  const onClickBalance = useCallback(
    ({
      chain_id,
      denom,
      ticker,
    }: {
      chain_id: string
      denom: string
      ticker: string
    }) => onSelectToken({ chainId: chain_id, denom, ticker }),
    [onSelectToken],
  )
  const [openItem, setOpenItem] = useAtom(singleBalanceAccordionAtom)

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omitting dependencies to avoid infinite re-renders
  const onClick = useCallback(() => {
    setAccordionValue((prev) => {
      if (prev !== 'your-balances') {
        return 'your-balances'
      }

      return ''
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [isDelayedOpen] = useDebounce(isOpen, 300)

  if (balancesSummary.length === 0) return <div ref={ref} />

  return (
    <Accordion
      ref={ref}
      type='single'
      collapsible
      defaultValue='your-balances'
      value={accordionValue}
    >
      <AccordionItem value='your-balances' className='border-none'>
        <div className='flex items-center justify-between'>
          <AccordionTrigger
            onClick={onClick}
            className='group flex items-center justify-start px-4 pt-4 pb-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:-outline-offset-2 rounded-xl'
          >
            <ChevronRight className='group-data-[state=open]:w-0 group-data-[state=closed]:w-6 h-6 text-tertiary group-data-[state=open]:rotate-90 transition-all ease-default group-data-[state=open]:group-hover:w-6' />
            <span className='text-secondary text-sm font-normal'>
              {filteredNetwork
                ? `Your balances on ${filteredNetwork.chain_name}`
                : 'Your balances'}
            </span>
          </AccordionTrigger>
          <AnimatePresence>
            {filteredNetwork && (
              <motion.div
                className='pt-2 pr-4'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.3,
                  ease: FRAMER_MOTION_TOKEN_SELECT_EASING,
                }}
              >
                <Button
                  type='button'
                  size='sm'
                  variant='ghost'
                  onClick={() => setFilteredNetwork(null)}
                >
                  Show all
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <AccordionContent
          className={cn({
            '!duration-0': direction === 'to' && !isDelayedOpen,
          })}
        >
          <BalanceSummariesAccordions
            onClickBalance={onClickBalance}
            balancesSummary={balancesSummary}
            externalOpenItem={openItem}
            setExternalOpenItem={setOpenItem}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

const YourBalances = memo(YourBalances_)

function PopularTokens_({
  items,
  tokens,
  onSelectToken,
  isLoading,
  noSearchResults,
  query,
}: {
  items: VirtualItem[]
  tokens: PopularTokenEntry[]
  onSelectToken: OnSelectToken
  isLoading: boolean
  noSearchResults: boolean
  query: string
}) {
  const { direction, filteredNetwork } = useTokenSelection()

  if (isLoading) {
    return (
      <div className='flex flex-col pt-4 items-center justify-center'>
        {Array(4)
          .fill(null)
          .map((_, index) => (
            <div
              key={index}
              className='flex p-4 w-full items-center justify-between'
            >
              <div className='flex items-center gap-4'>
                <Skeleton className='size-12 rounded-full' />
                <div className='flex flex-col gap-2'>
                  <Skeleton className='w-14 h-3' delay={`${index * 100}ms`} />
                  <Skeleton className='w-8 h-3' delay={`${index * 100}ms`} />
                </div>
              </div>
              <div className='flex flex-col items-end gap-2'>
                <Skeleton className='w-14 h-3' delay={`${index * 200}ms`} />
                <Skeleton className='w-8 h-3' delay={`${index * 200}ms`} />
              </div>
            </div>
          ))}
      </div>
    )
  }

  if (noSearchResults) return null

  return (
    <>
      <div className='flex items-center px-4 pt-4 pb-2'>
        <span className='text-secondary text-sm'>
          {query
            ? 'Search results'
            : filteredNetwork
              ? `Popular tokens on ${filteredNetwork.chain_name}`
              : 'Popular tokens'}
        </span>
      </div>
      <TooltipProvider>
        {items.map((virtualRow) => (
          <PopularTokenItem
            key={virtualRow.key}
            onSelectToken={onSelectToken}
            tokens={tokens}
            virtualRow={virtualRow}
            direction={direction}
            isSearching={query !== ''}
          />
        ))}
      </TooltipProvider>
    </>
  )
}

const PopularTokens = memo(PopularTokens_)
